import React from "react";
import {
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { Organisation, User, UserType } from "../../../types";
import TextCopyChip from "../../../components/chips/text-copy-chip";
import EditableRoleChip from "../../../components/chips/editable-role-chip";
import TeamMembershipItem from "./team-membership-item";
import { UserAvatar } from "../../../components/avatars";

interface UserCardProps {
  user: User;
  teams: Organisation[];
}

const UserCard: React.FC<UserCardProps> = ({ user, teams }) => {
  const displayName = user.fullName || "Unknown User";
  const isGuest = user.userType === UserType.Guest;

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
      <Paper
        sx={{ p: 2, borderRadius: "1rem", position: "relative" }}
        elevation={3}
      >
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1,
          }}
        >
          <EditableRoleChip user={user} />
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 240 }}
          >
            {isGuest ? (
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  backgroundColor: "#055480",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PersonIcon sx={{ fontSize: 24 }} />
              </Box>
            ) : (
              <UserAvatar text={user.fullName || user.email} />
            )}

            <Stack spacing={0.15}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  lineHeight: 1.1,
                  fontSize: { xs: "0.92rem", sm: "1rem" },
                }}
              >
                {displayName}
              </Typography>

              {user.jobTitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.1, fontSize: "0.8rem" }}
                >
                  {user.jobTitle}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {user.email ? (
            <TextCopyChip text={user.email} />
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 0.5, textAlign: "center" }}
            >
              No email
            </Typography>
          )}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={1} sx={{ pr: 0.5 }}>
          {teams.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 0.5, textAlign: "center" }}
            >
              Not part of any teams
            </Typography>
          ) : (
            teams.map((team) => {
              return (
                <TeamMembershipItem
                  key={`${team.organisationId}-${user.userId}`}
                  user={user}
                  team={team}
                />
              );
            })
          )}
        </Stack>
      </Paper>
    </Grid>
  );
};

export default UserCard;
